import { db, serverTimestamp } from "./firebase";
import i18n from "@/i18n";
import {
  collection,
  collectionGroup,
  documentId,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  deleteDoc,
  limit,
  Timestamp,
} from "firebase/firestore";

/* =========================
   EVENT INVITES (pendientes)
========================= */

// Lista solicitudes pendientes del usuario
export async function listMyPendingEventInvites(uid) {
  const qInv = query(
    collection(db, "users", uid, "eventInvites"),
    where("status", "==", "pending")
  );
  const snap = await getDocs(qInv);
  const invites = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // orden por fecha (si existe)
  invites.sort(
    (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
  );

  return invites;
}

// Aceptar solicitud -> crea eventAccess accepted (y opcionalmente marca invite accepted)
export async function acceptEventInvite({ uid, eventId }) {
  if (!uid) throw new Error(i18n.t("errors.notAuthenticated"));
  if (!eventId) throw new Error(i18n.t("errors.missingEventId"));

  const inviteRef = doc(db, "users", uid, "eventInvites", eventId);
  const inviteSnap = await getDoc(inviteRef);
  if (!inviteSnap.exists()) throw new Error(i18n.t("errors.inviteNotFound"));

  const inv = inviteSnap.data() || {};

  // ✅ Permitimos aceptar si estaba pending O declined
  if (inv.status === "accepted") return;
  if (inv.status !== "pending" && inv.status !== "declined") {
    throw new Error(i18n.t("errors.invalidInviteState"));
  }

  const access = inv.access === "participate" ? "participate" : "view";

  // ✅ ID fijo para evitar duplicados (no dependemos de groupId)
  const accId = `${eventId}_${uid}_invite`;

  const batch = writeBatch(db);

  batch.set(doc(db, "eventAccess", accId), {
    eventId,
    userId: uid,
    groupId: null,
    access, // "view"|"participate"
    status: "accepted",
    source: "invite",
    sourceGroupIds: Array.isArray(inv.sourceGroupIds) ? inv.sourceGroupIds : [],
    createdAt: serverTimestamp(),
  });

  batch.update(inviteRef, {
    status: "accepted",
    respondedAt: serverTimestamp(),
  });

  await batch.commit();
}

// Rechazar solicitud -> solo marca declined (no aparece en calendario)
export async function declineEventInvite({ uid, eventId }) {
  if (!uid) throw new Error(i18n.t("errors.notAuthenticated"));

  const inviteRef = doc(db, "users", uid, "eventInvites", eventId);
  const inviteSnap = await getDoc(inviteRef);
  if (!inviteSnap.exists()) return;

  const inv = inviteSnap.data();
  if (inv.status !== "pending" && inv.status !== "declined") return;

  await updateDoc(inviteRef, {
    status: "declined",
    respondedAt: serverTimestamp(),
  });
}

/* =========================
   PERMISOS (helpers)
========================= */

// ¿Es owner del grupo?
export async function isGroupOwner({ groupId, uid }) {
  const g = await getDoc(doc(db, "groups", groupId));
  if (!g.exists()) return false;
  return g.data().ownerId === uid;
}

// ¿Es admin del grupo (por membresía)?
export async function isGroupAdmin({ groupId, uid }) {
  const m = await getDoc(doc(db, "groups", groupId, "members", uid));
  if (!m.exists()) return false;
  const data = m.data();
  return data.status === "active" && data.role === "admin";
}

// ¿Puede borrar grupo? (owner o admin)
export async function canDeleteGroup({ groupId, uid }) {
  if (!uid) return false;
  if (await isGroupOwner({ groupId, uid })) return true;
  if (await isGroupAdmin({ groupId, uid })) return true;
  return false;
}

// ¿Puede borrar evento? (creador del evento, o admin del grupo si quieres)
export async function canDeleteEvent({ eventId, uid }) {
  if (!uid) return false;

  const ev = await getDoc(doc(db, "events", eventId));
  if (!ev.exists()) return false;

  const data = ev.data();

  // creador del evento
  if (data.createdBy === uid) return true;

  // opcional: admin del grupo (si el evento pertenece a un grupo)
  if (data.groupId) {
    if (await isGroupOwner({ groupId: data.groupId, uid })) return true;
    if (await isGroupAdmin({ groupId: data.groupId, uid })) return true;
  }

  return false;
}

/* =========================
   BORRAR EVENTO
========================= */

export async function deleteEvent({ eventId, uid }) {
  if (!uid) throw new Error(i18n.t("errors.notAuthenticated"));

  const ref = doc(db, "events", eventId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error(i18n.t("errors.eventNotFound"));

  const data = snap.data();

  // ✅ SOLO el creador puede borrar
  if (data.createdBy !== uid) {
    throw new Error(i18n.t("errors.deleteEventForbidden"));
  }

  // 1) borrar el evento (operación principal).
  // Si esto falla, debemos propagar el error.
  await deleteDoc(ref);

  // 2) limpieza secundaria best-effort (no bloquea el borrado principal).
  // OJO: Firestore NO permite collectionGroup + delete directo sin leer.
  const cleanupTasks = [
    (async () => {
      const qAcc = query(
        collection(db, "eventAccess"),
        where("eventId", "==", eventId)
      );
      const sAcc = await getDocs(qAcc);
      if (!sAcc.empty) {
        const batch = writeBatch(db);
        sAcc.forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }
    })(),
    (async () => {
      const qInv = query(
        collectionGroup(db, "eventInvites"),
        where("eventId", "==", eventId)
      );
      const sInv = await getDocs(qInv);
      if (!sInv.empty) {
        const batch = writeBatch(db);
        sInv.forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }
    })(),
  ];

  const cleanupResults = await Promise.allSettled(cleanupTasks);
  cleanupResults.forEach((res) => {
    if (res.status === "rejected") {
      console.warn("deleteEvent cleanup warning:", res.reason);
    }
  });
}

/* =========================
   BORRAR GRUPO (y limpieza)
========================= */

async function deleteAllDocsInCollection(pathSegments) {
  const colRef = collection(db, ...pathSegments);
  const snap = await getDocs(colRef);
  if (snap.empty) return;

  // batch max ~500
  let batch = writeBatch(db);
  let count = 0;

  for (const d of snap.docs) {
    batch.delete(d.ref);
    count++;
    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }
  if (count) await batch.commit();
}
export async function leaveGroup({ groupId, uid }) {
  if (!groupId) throw new Error(i18n.t("errors.missingGroupId"));
  if (!uid) throw new Error(i18n.t("errors.missingUid"));

  // 1) borrar membresía real
  const memberRef = doc(db, "groups", groupId, "members", uid);

  // 2) borrar mirror (la UI lista desde aquí)
  const mirrorRef = doc(db, "groupMembers", `${groupId}_${uid}`);

  // 3) borrar accesos a eventos que venían por este grupo
  const accQ = query(
    collection(db, "eventAccess"),
    where("userId", "==", uid),
    where("groupId", "==", groupId)
  );
  const accSnap = await getDocs(accQ);

  // batch (max ~500 ops)
  let batch = writeBatch(db);
  let count = 0;

  batch.delete(memberRef);
  count++;

  batch.delete(mirrorRef);
  count++;

  for (const d of accSnap.docs) {
    batch.delete(d.ref);
    count++;
    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }

  if (count) await batch.commit();
}


export async function deleteGroup({ groupId, uid }) {
  const allowed = await canDeleteGroup({ groupId, uid });
  if (!allowed) throw new Error(i18n.t("errors.deleteGroupForbidden"));

  // 1) Obtener eventos que tengan este grupo en groupIds (modelo nuevo)
  const evQ = query(
    collection(db, "events"),
    where("groupIds", "array-contains", groupId)
  );
  const evSnap = await getDocs(evQ);

  // 2) Para cada evento: desasociar el grupo, limpiar eventAccess del grupo,
  //    y si se queda sin grupos -> borrar evento + limpiar TODOS sus eventAccess
  for (const evDoc of evSnap.docs) {
    const eventId = evDoc.id;
    const ev = evDoc.data() || {};

    const prevGroupIds = Array.isArray(ev.groupIds) ? ev.groupIds : [];
    const nextGroupIds = prevGroupIds.filter((g) => g !== groupId);

    const prevShare =
      ev.shareByGroup && typeof ev.shareByGroup === "object"
        ? ev.shareByGroup
        : {};
    const nextShare = { ...prevShare };
    delete nextShare[groupId];

    // 2a) Borrar permisos eventAccess SOLO de este grupo (view/participate)
    {
      const accQ = query(
        collection(db, "eventAccess"),
        where("eventId", "==", eventId),
        where("groupId", "==", groupId)
      );
      const accSnap = await getDocs(accQ);

      if (!accSnap.empty) {
        const batch = writeBatch(db);
        accSnap.forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }
    }

    // 2b) Si el evento aún tiene otros grupos, solo lo actualizamos
    if (nextGroupIds.length > 0) {
      await updateDoc(doc(db, "events", eventId), {
        groupIds: nextGroupIds,
        shareByGroup: nextShare,
        updatedAt: serverTimestamp(),
      });
      continue;
    }

    // 2c) Si ya NO quedan grupos, borramos el evento completo
    await deleteDoc(doc(db, "events", eventId));

    // 2d) ...y limpiamos TODOS los eventAccess de ese evento
    {
      const allAccQ = query(
        collection(db, "eventAccess"),
        where("eventId", "==", eventId)
      );
      const allAccSnap = await getDocs(allAccQ);

      if (!allAccSnap.empty) {
        const batch = writeBatch(db);
        allAccSnap.forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }
    }
  }

  // 3) Borrar miembros (subcolección groups/{groupId}/members)
  await deleteAllDocsInCollection(["groups", groupId, "members"]);

  // 4) Borrar mirrors de memberships (en tu proyecto: groupMembers)
  {
    const mirrorQ = query(
      collection(db, "groupMembers"),
      where("groupId", "==", groupId)
    );
    const mirrorSnap = await getDocs(mirrorQ);

    if (!mirrorSnap.empty) {
      let batch = writeBatch(db);
      let count = 0;
      for (const d of mirrorSnap.docs) {
        batch.delete(d.ref);
        count++;
        if (count >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          count = 0;
        }
      }
      if (count) await batch.commit();
    }
  }

  // 5) Borrar el grupo
  await deleteDoc(doc(db, "groups", groupId));
}

/* =========================
   USERS (perfil)
========================= */

export async function upsertUserProfile(user) {
  // user = firebaseAuth.currentUser
  if (!user?.uid) return;

  const userRef = doc(db, "users", user.uid);
  const snap = await getDoc(userRef);
  const existing = snap.exists() ? snap.data() || {} : {};
  const hasEventInviteNotifSetting =
    typeof existing?.settings?.notifications?.eventInviteRequests ===
    "boolean";

  const payload = {
    displayName: user.displayName || user.email?.split("@")[0] || "Usuario",
    email: user.email || "",
    photoURL: user.photoURL || "",
    updatedAt: serverTimestamp(),
  };

  if (!hasEventInviteNotifSetting) {
    payload.settings = {
      notifications: {
        eventInviteRequests: true,
      },
    };
  }

  await setDoc(
    userRef,
    payload,
    { merge: true }
  );
}

export async function getMyNotificationSettings(uid) {
  if (!uid) {
    return {
      eventInviteRequests: true,
    };
  }

  const snap = await getDoc(doc(db, "users", uid));
  const data = snap.exists() ? snap.data() || {} : {};

  return {
    eventInviteRequests:
      typeof data?.settings?.notifications?.eventInviteRequests === "boolean"
        ? data.settings.notifications.eventInviteRequests
        : true,
  };
}

export async function setEventInviteRequestNotifications({ uid, enabled }) {
  if (!uid) throw new Error(i18n.t("errors.notAuthenticated"));

  await setDoc(
    doc(db, "users", uid),
    {
      settings: {
        notifications: {
          eventInviteRequests: !!enabled,
        },
      },
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function upsertUserPushToken({ uid, token, platform }) {
  if (!uid) throw new Error(i18n.t("errors.notAuthenticated"));
  if (!token) throw new Error(i18n.t("errors.missingPushToken"));

  await setDoc(
    doc(db, "users", uid, "pushTokens", token),
    {
      token,
      platform: platform || "unknown",
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function removeUserPushToken({ uid, token }) {
  if (!uid || !token) return;
  await deleteDoc(doc(db, "users", uid, "pushTokens", token));
}

/* =========================
   GROUPS
========================= */

export async function createGroup({ uid, name, type }) {
  const groupRef = await addDoc(collection(db, "groups"), {
    name,
    // type: type || "family",
    ownerId: uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // owner membership in subcollection
  await setDoc(doc(db, "groups", groupRef.id, "members", uid), {
    userId: uid,
    role: "admin",
    status: "active",
    createdAt: serverTimestamp(),
  });

  // ✅ mirror membership for listing
  await createMembershipMirror({
    groupId: groupRef.id,
    userId: uid,
    role: "admin",
  });

  return groupRef.id;
}

export async function listMyGroups(uid) {
  const q = query(collection(db, "groupMembers"), where("userId", "==", uid));
  const snap = await getDocs(q);
  const groupIds = [...new Set(
    snap.docs
      .map((d) => d.data().groupId)
      .filter((id) => typeof id === "string" && id.trim().length)
  )];

  if (!groupIds.length) return [];

  const chunks = chunkArray(groupIds, 10);
  const groups = [];
  const reads = chunks.map((c) =>
    getDocs(query(collection(db, "groups"), where(documentId(), "in", c)))
  );
  const snaps = await Promise.all(reads);
  snaps.forEach((gs) => {
    gs.forEach((docu) => groups.push({ id: docu.id, ...docu.data() }));
  });

  groups.sort(
    (a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0)
  );
  return groups;
}

export async function createMembershipMirror({
  groupId,
  userId,
  role = "member",
  invitedViaToken = null,
}) {
  const id = `${groupId}_${userId}`;
  const payload = {
    groupId,
    userId,
    role,
    status: "active",
    createdAt: serverTimestamp(),
  };

  if (invitedViaToken) {
    payload.invitedViaToken = invitedViaToken;
  }

  await setDoc(doc(db, "groupMembers", id), payload);
}

/* =========================
   MEMBERS: listar miembros + perfil
========================= */

export async function listGroupMembersWithProfiles(groupId) {
  // 1) memberships desde mirror
  const q = query(
    collection(db, "groupMembers"),
    where("groupId", "==", groupId)
  );
  const snap = await getDocs(q);
  const members = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

  if (!members.length) return [];

  // 2) cargar perfiles users/{uid} por lotes
  const uniqueUids = [...new Set(
    members
      .map((m) => m.userId)
      .filter((id) => typeof id === "string" && id.trim().length)
  )];
  const profiles = new Map();
  
  if (uniqueUids.length) {
    try {
      const userChunks = chunkArray(uniqueUids, 10);
      const userReads = userChunks.map((chunk) =>
        getDocs(query(collection(db, "users"), where(documentId(), "in", chunk)))
      );
      const userSnaps = await Promise.all(userReads);

      userSnaps.forEach((snapUsers) => {
        snapUsers.forEach((us) => {
          profiles.set(us.id, { uid: us.id, ...us.data() });
        });
      });
    } catch (_err) {
      // fallback robusto si falla la consulta por lotes
      await Promise.all(
        uniqueUids.map(async (uid) => {
          const us = await getDoc(doc(db, "users", uid));
          if (us.exists()) profiles.set(uid, { uid, ...us.data() });
        })
      );
    }
  }

  uniqueUids.forEach((uid) => {
    if (profiles.has(uid)) return;
    profiles.set(uid, {
      uid,
      displayName: "Usuario",
      photoURL: "",
      email: "",
    });
  });

  // 3) combinar
  return members.map((m) => ({
    ...m,
    profile: profiles.get(m.userId) || {
      uid: m.userId,
      displayName: "Usuario",
      photoURL: "",
      email: "",
    },
  }));
}

/* =========================
   GROUPS + MEMBERS (para la UI)
========================= */

export async function listMyGroupsWithMembers(uid) {
  const groups = await listMyGroups(uid);
  if (!groups.length) return [];

  // carga miembros de cada grupo en paralelo
  const membersByGroup = await Promise.all(
    groups.map(async (g) => {
      const members = await listGroupMembersWithProfiles(g.id);
      // orden: admins primero
      members.sort(
        (a, b) => (a.role === "admin" ? -1 : 1) - (b.role === "admin" ? -1 : 1)
      );
      return [g.id, members];
    })
  );

  const map = new Map(membersByGroup);

  return groups.map((g) => ({
    ...g,
    members: map.get(g.id) || [],
  }));
}

/* =========================
   INVITES
========================= */

export async function generateInviteToken({ uid, groupId }) {
  const token = randomToken();
  await setDoc(doc(db, "groupInvites", token), {
    groupId,
    createdBy: uid,
    createdAt: serverTimestamp(),
    expiresAt: Timestamp.fromDate(
      new Date(Date.now() + 1000 * 60 * 60 * 24 * 7)
    ),
  });
  return token;
}

export async function acceptInviteToken({ uid, token }) {
  const inviteRef = doc(db, "groupInvites", token);
  const snap = await getDoc(inviteRef);
  if (!snap.exists()) throw new Error(i18n.t("errors.invalidInvite"));

  const invite = snap.data();
  if (invite.expiresAt?.toMillis && invite.expiresAt.toMillis() < Date.now()) {
    throw new Error(i18n.t("errors.expiredInvite"));
  }

  const groupId = invite.groupId;

  // Write membership in group subcollection
  await setDoc(doc(db, "groups", groupId, "members", uid), {
    userId: uid,
    role: "member",
    status: "active",
    invitedViaToken: token,
    createdAt: serverTimestamp(),
  });

  // Mirror for easy querying
  await createMembershipMirror({
    groupId,
    userId: uid,
    role: "member",
    invitedViaToken: token,
  });

  return groupId;
}

/* =========================
   EVENTS
========================= */

export async function createEvent({
  uid,
  title,
  description,
  startAt,
  endAt,

  groupIds = [],
  accessMode = "view",

  // ✅ nuevo: viene de EventDialog (array [{groupId, accessMode}])
  shareByGroup = [], // [{ groupId, accessMode: "view"|"participate" }]
  allDay = false,
}) {
  // 1) Normalizar shareByGroup -> mapa
  const shareMap = {};
  if (Array.isArray(shareByGroup) && shareByGroup.length) {
    for (const x of shareByGroup) {
      if (
        x?.groupId &&
        (x.accessMode === "view" || x.accessMode === "participate")
      ) {
        shareMap[x.groupId] = x.accessMode;
      }
    }
  }

  // compat: si no llega shareByGroup, caemos al modelo viejo
  if (
    !Object.keys(shareMap).length &&
    Array.isArray(groupIds) &&
    groupIds.length
  ) {
    for (const gid of groupIds) shareMap[gid] = accessMode || "view";
  }

  const finalGroupIds = Object.keys(shareMap);
  const requestedBy = await getUserInviteProfile(uid);
  const groupPreviews = await getGroupInvitePreviews(finalGroupIds);

  // 2) Crear doc del evento guardando shareByGroup
  const eventRef = await addDoc(collection(db, "events"), {
    title,
    description: description || "",
    startAt: Timestamp.fromDate(startAt),
    endAt: Timestamp.fromDate(endAt),
    createdBy: uid,

    groupIds: finalGroupIds,
    shareByGroup: shareMap,
    allDay: !!allDay,

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // 3) Access del creador (opcional, sirve para listVisibleEvents)
  await setDoc(doc(db, "eventAccess", `${eventRef.id}_${uid}_creator`), {
    eventId: eventRef.id,
    userId: uid,
    groupId: null,
    access: "participate",
    status: "accepted",
    source: "creator",
    createdAt: serverTimestamp(),
  });

  // 4) Permisos / invites por miembros
  const batch = writeBatch(db);

  for (const gid of finalGroupIds) {
    const mode = shareMap[gid]; // "view"|"participate"

    const membersQ = query(
      collection(db, "groupMembers"),
      where("groupId", "==", gid)
    );
    const membersSnap = await getDocs(membersQ);

    const members = membersSnap.docs
      .map((d) => d.data().userId)
      .filter((x) => x && x !== uid);

    for (const muid of members) {
      if (mode === "view") {
        batch.set(doc(db, "eventAccess", `${eventRef.id}_${muid}_${gid}`), {
          eventId: eventRef.id,
          userId: muid,
          groupId: gid,
          access: "view",
          status: "accepted",
          source: "group_view",
          createdAt: serverTimestamp(),
        });
      } else {
        const invitePayload = {
          eventId: eventRef.id,
          groupIds: finalGroupIds,
          sourceGroupIds: [gid],
          access: "participate",
          status: "pending",
          createdBy: uid,
          requestedBy,
          eventPreview: {
            title,
            description: description || "",
            startAt: Timestamp.fromDate(startAt),
            endAt: Timestamp.fromDate(endAt),
          },
          createdAt: serverTimestamp(),
        };

        if (groupPreviews[gid]?.name) {
          invitePayload.groupPreview = groupPreviews[gid];
        }

        batch.set(
          doc(db, "users", muid, "eventInvites", eventRef.id),
          invitePayload,
          { merge: true }
        );
      }
    }
  }

  await batch.commit();

  return eventRef.id;
}

export async function updateEvent({
  uid,
  eventId,
  title,
  description,
  startAt,
  endAt,
  groupIds = [],
  accessMode = "view",
  shareByGroup = [],
  allDay = false,
}) {
  if (!uid) throw new Error(i18n.t("errors.notAuthenticated"));
  if (!eventId) throw new Error(i18n.t("errors.missingEventId"));

  const eventRef = doc(db, "events", eventId);
  const eventSnap = await getDoc(eventRef);
  if (!eventSnap.exists()) throw new Error(i18n.t("errors.eventNotFound"));

  const existing = eventSnap.data() || {};
  if (existing.createdBy !== uid) {
    throw new Error(i18n.t("errors.updateEventForbidden"));
  }

  const shareMap = normalizeExistingEventShare(existing);
  const finalGroupIds = Object.keys(shareMap);
  const changedFields = detectEventChangedFields({
    before: existing,
    after: { title, description, startAt, endAt, allDay },
  });
  let participantUids = [];
  if (changedFields.length) {
    try {
      participantUids = await listAcceptedEventParticipantUids({ eventId, uid });
    } catch (err) {
      console.warn("updateEvent participant lookup warning:", err);
    }
  }

  await updateDoc(eventRef, {
    title,
    description: description || "",
    startAt: Timestamp.fromDate(startAt),
    endAt: Timestamp.fromDate(endAt),
    groupIds: finalGroupIds,
    shareByGroup: shareMap,
    allDay: !!allDay,
    updatedAt: serverTimestamp(),
  });

  const secondaryTasks = [
    participantUids.length
      ? createEventChangeNotifications({
          eventId,
          editorUid: uid,
          participantUids,
          previousEvent: existing,
          title,
          description,
          startAt,
          endAt,
          allDay,
          changedFields,
        })
      : Promise.resolve(),
    rebuildEventSharing({
      eventId,
      uid,
      title,
      description,
      startAt,
      endAt,
      finalGroupIds,
      shareMap,
    }),
  ];

  const secondaryResults = await Promise.allSettled(secondaryTasks);
  secondaryResults.forEach((res) => {
    if (res.status === "rejected") {
      console.warn("updateEvent secondary write warning:", res.reason);
    }
  });

  return eventId;
}

export async function listVisibleEvents({ uid, selectedGroupIds = [] }) {
  const qAccessUser = query(
    collection(db, "eventAccessUser"),
    where("userId", "==", uid)
  );
  const qLegacyAccess = query(
    collection(db, "eventAccess"),
    where("userId", "==", uid)
  );
  const qCreatedBy = query(
    collection(db, "events"),
    where("createdBy", "==", uid)
  );

  const [accessUserSnap, legacyAccessSnap, createdBySnap] = await Promise.all([
    getDocs(qAccessUser),
    getDocs(qLegacyAccess),
    getDocs(qCreatedBy),
  ]);

  const accessUserDocs = accessUserSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const legacyAccessDocs = legacyAccessSnap.docs.map((d) => d.data());

  const accessByEvent = new Map();

  for (const a of accessUserDocs) {
    if (!a?.eventId) continue;
    if (a.status && a.status !== "accepted") continue;

    accessByEvent.set(a.eventId, {
      accessByGroup:
        a.accessByGroup && typeof a.accessByGroup === "object"
          ? a.accessByGroup
          : {},
      effectiveAccess:
        a.effectiveAccess === "participate" || a.effectiveAccess === "view"
          ? a.effectiveAccess
          : null,
    });
  }

  for (const a of legacyAccessDocs) {
    if (!a?.eventId || a.status !== "accepted") continue;

    if (!accessByEvent.has(a.eventId)) {
      accessByEvent.set(a.eventId, {
        accessByGroup: {},
        effectiveAccess: null,
      });
    }

    const rec = accessByEvent.get(a.eventId);
    if (a.groupId) {
      rec.accessByGroup[a.groupId] = a.access;
    }
    if (a.access === "participate") rec.effectiveAccess = "participate";
    else if (!rec.effectiveAccess && a.access === "view") {
      rec.effectiveAccess = "view";
    }
  }

  // Red de seguridad retrocompatible: incluir siempre eventos creados por el usuario.
  const createdEvents = createdBySnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  for (const ev of createdEvents) {
    if (!accessByEvent.has(ev.id)) {
      accessByEvent.set(ev.id, {
        accessByGroup: {},
        effectiveAccess: "participate",
      });
    }
  }

  const eventIds = [...accessByEvent.keys()];

  if (!eventIds.length) return [];

  const eventsById = new Map(createdEvents.map((ev) => [ev.id, ev]));
  const remainingIds = eventIds.filter((id) => !eventsById.has(id));

  const chunks = chunkArray([...new Set(remainingIds)], 10);
  const chunkReads = chunks
    .filter((c) => c.length)
    .map((c) => getDocs(query(collection(db, "events"), where("__name__", "in", c))));

  const chunkSnaps = await Promise.all(chunkReads);
  chunkSnaps.forEach((evSnap) => {
    evSnap.forEach((d) => eventsById.set(d.id, { id: d.id, ...d.data() }));
  });

  const events = [...eventsById.values()];

  const filtered = events.filter((ev) => {
    const evGroupIds = ev.groupIds || [];
    if (!evGroupIds.length) return true;
    if (!selectedGroupIds.length) return true;
    return evGroupIds.some((g) => selectedGroupIds.includes(g));
  });

  filtered.sort(
    (a, b) => (a.startAt?.seconds || 0) - (b.startAt?.seconds || 0)
  );

  return filtered.map((ev) => {
    const rec = accessByEvent.get(ev.id) || {
      accessByGroup: {},
      effectiveAccess: null,
    };

    return {
      ...ev,
      accessByGroup: rec.accessByGroup,
      access: rec.effectiveAccess ? { access: rec.effectiveAccess } : null,
    };
  });
}

/* =========================
   ✅ BORRAR CUENTA (Firestore cleanup)
   - esto NO borra Firebase Auth, solo Firestore
   - úsalo justo ANTES de auth.currentUser.delete()
========================= */

export async function deleteMyAccountFirestoreData({ uid }) {
  if (!uid) throw new Error(i18n.t("errors.notAuthenticated"));

  // 0) Si es owner de grupos: los eliminamos (limpia eventos/permisos)
  {
    const gq = query(collection(db, "groups"), where("ownerId", "==", uid));
    const gs = await getDocs(gq);
    for (const g of gs.docs) {
      // deleteGroup ya valida permisos (owner/admin)
      await deleteGroup({ groupId: g.id, uid });
    }
  }

  // 1) Borrar eventos creados por el usuario (borra eventAccess + invites)
  {
    const evQ = query(collection(db, "events"), where("createdBy", "==", uid));
    const evSnap = await getDocs(evQ);
    for (const ev of evSnap.docs) {
      await deleteEvent({ eventId: ev.id, uid });
    }
  }

  // 2) Salir de grupos (membresía espejo + subcolección members)
  {
    const mQ = query(collection(db, "groupMembers"), where("userId", "==", uid));
    const mSnap = await getDocs(mQ);

    // borramos mirror + el doc en groups/{groupId}/members/{uid}
    let batch = writeBatch(db);
    let count = 0;

    for (const d of mSnap.docs) {
      const data = d.data() || {};
      const groupId = data.groupId;

      batch.delete(d.ref);
      count++;

      if (groupId) {
        batch.delete(doc(db, "groups", groupId, "members", uid));
        count++;
      }

      if (count >= 430) {
        await batch.commit();
        batch = writeBatch(db);
        count = 0;
      }
    }
    if (count) await batch.commit();
  }

  // 3) Borrar eventAccess del usuario (los eventos que veía/tenía acceso)
  {
    const accQ = query(collection(db, "eventAccess"), where("userId", "==", uid));
    const accSnap = await getDocs(accQ);

    if (!accSnap.empty) {
      let batch = writeBatch(db);
      let count = 0;

      for (const d of accSnap.docs) {
        batch.delete(d.ref);
        count++;
        if (count >= 450) {
          await batch.commit();
          batch = writeBatch(db);
          count = 0;
        }
      }
      if (count) await batch.commit();
    }
  }

  // 4) Borrar mis invites pendientes/antiguas: users/{uid}/eventInvites/*
  await deleteAllDocsInCollection(["users", uid, "eventInvites"]);

  // (Opcional) Si tienes más subcolecciones en users/{uid}, bórralas aquí:
  // await deleteAllDocsInCollection(["users", uid, "X"]);

  // 5) Borrar perfil users/{uid}
  await deleteDoc(doc(db, "users", uid));
}

/* =========================
   helpers
========================= */

function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function normalizeEventShare({ groupIds = [], accessMode = "view", shareByGroup = [] }) {
  const shareMap = {};

  if (Array.isArray(shareByGroup) && shareByGroup.length) {
    for (const x of shareByGroup) {
      if (
        x?.groupId &&
        (x.accessMode === "view" || x.accessMode === "participate")
      ) {
        shareMap[x.groupId] = x.accessMode;
      }
    }
  }

  if (
    !Object.keys(shareMap).length &&
    Array.isArray(groupIds) &&
    groupIds.length
  ) {
    for (const gid of groupIds) shareMap[gid] = accessMode || "view";
  }

  return shareMap;
}

function normalizeExistingEventShare(event) {
  const share =
    event?.shareByGroup && typeof event.shareByGroup === "object"
      ? event.shareByGroup
      : {};
  const groupIds = Array.isArray(event?.groupIds) ? event.groupIds : [];
  const out = {};

  groupIds.forEach((gid) => {
    const id = String(gid);
    const mode = share[id];
    out[id] = mode === "participate" || mode === "view" ? mode : "participate";
  });

  return out;
}

function eventValueToMillis(value) {
  if (!value) return null;
  if (value?.toMillis) return value.toMillis();
  if (value?.seconds) return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

function detectEventChangedFields({ before, after }) {
  const changed = [];
  const beforeDescription = before?.description || "";
  const afterDescription = after?.description || "";

  if ((before?.title || "") !== (after?.title || "")) changed.push("title");
  if (beforeDescription !== afterDescription) changed.push("description");
  if (eventValueToMillis(before?.startAt) !== eventValueToMillis(after?.startAt)) {
    changed.push("startAt");
  }
  if (eventValueToMillis(before?.endAt) !== eventValueToMillis(after?.endAt)) {
    changed.push("endAt");
  }
  if (!!before?.allDay !== !!after?.allDay) changed.push("allDay");

  return changed;
}

async function listAcceptedEventParticipantUids({ eventId, uid }) {
  const snap = await getDocs(
    query(
      collection(db, "eventAccess"),
      where("eventId", "==", eventId),
      where("status", "==", "accepted")
    )
  );

  return [
    ...new Set(
      snap.docs
        .map((d) => d.data() || {})
        .filter((row) => row.access === "participate")
        .map((row) => row.userId)
        .filter((userId) => userId && userId !== uid)
    ),
  ];
}

async function createEventChangeNotifications({
  eventId,
  editorUid,
  participantUids,
  previousEvent,
  title,
  description,
  startAt,
  endAt,
  allDay,
  changedFields,
}) {
  let batch = writeBatch(db);
  let count = 0;
  const editId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  for (const userId of participantUids) {
    batch.set(doc(db, "eventChangeNotifications", `${eventId}_${userId}_${editId}`), {
      eventId,
      userId,
      editorUid,
      changedFields,
      previousPreview: {
        title: previousEvent?.title || "",
        description: previousEvent?.description || "",
        startAt: previousEvent?.startAt || null,
        endAt: previousEvent?.endAt || null,
        allDay: !!previousEvent?.allDay,
      },
      eventPreview: {
        title,
        description: description || "",
        startAt: Timestamp.fromDate(startAt),
        endAt: Timestamp.fromDate(endAt),
        allDay: !!allDay,
      },
      createdAt: serverTimestamp(),
    });
    count++;

    if (count >= 450) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  }

  if (count) await batch.commit();
}

async function rebuildEventSharing({
  eventId,
  uid,
  title,
  description,
  startAt,
  endAt,
  finalGroupIds,
  shareMap,
}) {
  const requestedBy = await getUserInviteProfile(uid);
  const groupPreviews = await getGroupInvitePreviews(finalGroupIds);

  const existingInvitesSnap = await getDocs(
    query(collectionGroup(db, "eventInvites"), where("eventId", "==", eventId))
  );
  const existingInviteByUser = new Map();
  existingInvitesSnap.forEach((d) => {
    const inviteUid = d.ref.parent?.parent?.id;
    if (inviteUid) existingInviteByUser.set(inviteUid, { ref: d.ref, data: d.data() || {} });
  });

  const accessSnap = await getDocs(
    query(collection(db, "eventAccess"), where("eventId", "==", eventId))
  );

  let batch = writeBatch(db);
  let count = 0;
  const commitIfNeeded = async (force = false) => {
    if (!count) return;
    if (count >= 430 || force) {
      await batch.commit();
      batch = writeBatch(db);
      count = 0;
    }
  };
  const batchSet = async (ref, payload, options) => {
    batch.set(ref, payload, options);
    count++;
    await commitIfNeeded();
  };
  const batchDelete = async (ref) => {
    batch.delete(ref);
    count++;
    await commitIfNeeded();
  };

  for (const d of accessSnap.docs) {
    await batchDelete(d.ref);
  }

  await batchSet(doc(db, "eventAccess", `${eventId}_${uid}_creator`), {
    eventId,
    userId: uid,
    groupId: null,
    access: "participate",
    status: "accepted",
    source: "creator",
    createdAt: serverTimestamp(),
  });

  const wantedParticipateUsers = new Map();

  for (const gid of finalGroupIds) {
    const mode = shareMap[gid];
    const membersQ = query(
      collection(db, "groupMembers"),
      where("groupId", "==", gid)
    );
    const membersSnap = await getDocs(membersQ);
    const members = membersSnap.docs
      .map((d) => d.data().userId)
      .filter((x) => x && x !== uid);

    for (const muid of members) {
      if (mode === "view") {
        await batchSet(doc(db, "eventAccess", `${eventId}_${muid}_${gid}`), {
          eventId,
          userId: muid,
          groupId: gid,
          access: "view",
          status: "accepted",
          source: "group_view",
          createdAt: serverTimestamp(),
        });
        continue;
      }

      const existingInvite = existingInviteByUser.get(muid)?.data || {};
      const sourceGroupIds = [
        ...new Set([...(existingInvite.sourceGroupIds || []), gid]),
      ].filter((sourceGid) => finalGroupIds.includes(sourceGid));
      wantedParticipateUsers.set(muid, sourceGroupIds.length ? sourceGroupIds : [gid]);

      if (existingInvite.status === "accepted") {
        await batchSet(doc(db, "eventAccess", `${eventId}_${muid}_invite`), {
          eventId,
          userId: muid,
          groupId: null,
          access: "participate",
          status: "accepted",
          source: "invite",
          sourceGroupIds: sourceGroupIds.length ? sourceGroupIds : [gid],
          createdAt: existingInvite.respondedAt || serverTimestamp(),
        });
      }
    }
  }

  for (const [muid, sourceGroupIds] of wantedParticipateUsers.entries()) {
    const firstGroupId = sourceGroupIds[0];
    const existingInvite = existingInviteByUser.get(muid)?.data || {};
    const nextStatus =
      existingInvite.status === "accepted" || existingInvite.status === "declined"
        ? existingInvite.status
        : "pending";
    const invitePayload = {
      eventId,
      groupIds: finalGroupIds,
      sourceGroupIds,
      access: "participate",
      status: nextStatus,
      createdBy: uid,
      requestedBy,
      eventPreview: {
        title,
        description: description || "",
        startAt: Timestamp.fromDate(startAt),
        endAt: Timestamp.fromDate(endAt),
      },
      updatedAt: serverTimestamp(),
    };

    if (!existingInvite.createdAt) {
      invitePayload.createdAt = serverTimestamp();
    }
    if (groupPreviews[firstGroupId]?.name) {
      invitePayload.groupPreview = groupPreviews[firstGroupId];
    }

    await batchSet(
      doc(db, "users", muid, "eventInvites", eventId),
      invitePayload,
      { merge: true }
    );
  }

  for (const [muid, invite] of existingInviteByUser.entries()) {
    if (!wantedParticipateUsers.has(muid)) {
      await batchDelete(invite.ref);
    }
  }

  await commitIfNeeded(true);
}

async function getUserInviteProfile(uid) {
  const fallback = {
    uid,
    displayName: "Usuario",
    email: "",
    photoURL: "",
  };

  if (!uid) return fallback;

  try {
    const snap = await getDoc(doc(db, "users", uid));
    if (!snap.exists()) return fallback;

    const data = snap.data() || {};
    return {
      uid,
      displayName:
        data.displayName || data.email?.split("@")[0] || fallback.displayName,
      email: data.email || "",
      photoURL: data.photoURL || "",
    };
  } catch (_err) {
    return fallback;
  }
}

async function getGroupInvitePreviews(groupIds) {
  const out = {};
  const ids = Array.isArray(groupIds) ? groupIds.filter(Boolean) : [];
  if (!ids.length) return out;

  await Promise.all(
    ids.map(async (groupId) => {
      try {
        const snap = await getDoc(doc(db, "groups", groupId));
        if (!snap.exists()) return;

        const data = snap.data() || {};
        out[groupId] = {
          id: groupId,
          name: data.name || data.title || data.label || "",
        };
      } catch (_err) {
        // Group names are helpful context, but invite creation should not fail without them.
      }
    })
  );

  return out;
}

function randomToken() {
  return (
    Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
  );
}
